import { CommonModule } from '@angular/common';
import { booleanAttribute, Component, forwardRef, Input, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR, NG_VALIDATORS, AbstractControl, ValidationErrors } from '@angular/forms';
import { ColorChromeModule } from 'ngx-color/chrome';
import { PopupComponent } from '../popup/PopupComponent';

@Component({
    selector: 'color-input',
    standalone: true,
    imports: [ColorChromeModule, CommonModule, FormsModule, PopupComponent],
    templateUrl: './ColorInputComponent.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ColorInputComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => ColorInputComponent),
            multi: true
        }
    ]
})
export class ColorInputComponent implements ControlValueAccessor
{
    @ViewChild('colorPopup') colorPopup!: PopupComponent;

    @Input() label = '';
    @Input() id = '';
    @Input({ transform: booleanAttribute }) required = false;

    value = '';
    touched = false;
    disabled = false;
    pickerColor = '#000000';

    onChange = (_: string) => { };
    onTouched = () => { };

    writeValue(value: string | null): void
    {
        this.value = value ?? '';
        this.touched = false;
    }

    registerOnChange(fn: (value: string) => void): void
    {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void
    {
        this.onTouched = fn;
    }

    setDisabledState(disabled: boolean): void
    {
        this.disabled = disabled;
    }

    updateValue(value: string)
    {
        this.value = value;
        this.onChange(value);
    }

    markTouched()
    {
        if (!this.touched)
        {
            this.touched = true;
            this.onTouched();
        }
    }

    colorChanged(event: any)
    {
        let color = '';
        if (event.color.rgb.a < 1)
        {
            color = `rgba(${event.color.rgb.r},${event.color.rgb.g},${event.color.rgb.b},${event.color.rgb.a})`;
        }
        else
        {
            color = event.color.hex;
        }

        this.value = color;
        this.pickerColor = color;
        this.onChange(color);
    }

    toggleColorPopup(event: MouseEvent)
    {
        event.preventDefault();

        // force update because ngx-color does not update color when input value changes programmatically
        this.pickerColor = this.value; 

        this.colorPopup.Toggle(event);
    }

    onInput(value: string)
    {
        this.value = value;
        this.onChange(value);
        this.markTouched();
    }

    validate(_: AbstractControl): ValidationErrors | null
    {
        if (!this.value)
        {
            return { required: true };
        }

        const valid = /^(#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})|rgba\(.+\))$/.test(this.value);
        return valid ? null : { colorFormat: true };
    }
}
